import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerRowTop - The header top row element
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerRowTop'];

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Whether the page is currently scroll-locked by an overlay (cart / search),
   * which pins <body> with position:fixed and resets scrollTop to 0.
   * @type {boolean}
   */
  #scrollLocked = false;

  /**
   * Watches <body> for the position:fixed scroll-lock so the header can hold its
   * place instead of being dragged to the document top.
   * @type {MutationObserver}
   */
  #scrollLockObserver = new MutationObserver(() => this.#syncScrollLock());

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    // While an overlay (cart / search) scroll-locks the page it pins <body> with
    // position:fixed, which makes scrollTop read 0. Ignore that so the header
    // doesn't flip out of its active (colored) state and snap back at the top.
    if (document.body.style.position === 'fixed') return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      // Pinned flush at top:0, so the header's own rect is always at 0 once stuck.
      // Derive the active (revealed-palette) state from the document scroll position
      // instead, which both removes the old 1px sticky hop and stays robust when the
      // header is absolutely positioned (transparent home state).
      const atPageTop = scrollTop <= 0;
      const nextState = atPageTop ? 'inactive' : 'active';

      if (this.dataset.stickyState !== nextState) {
        this.dataset.stickyState = nextState;
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      }

      if (atPageTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Called whenever <body>'s inline style changes. When an overlay scroll-locks
   * the page (body becomes position:fixed), pin this header's section wrapper at
   * the viewport top so it can't be dragged to the document top; restore it when
   * the lock releases. Inline styles win over Horizon's stylesheet rule.
   */
  #syncScrollLock = () => {
    const locked = document.body.style.position === 'fixed';
    const wrapper = this.closest('.shopify-section');
    if (!(wrapper instanceof HTMLElement)) return;

    if (locked) {
      // The overlay shifts <body> up by the scroll amount (top: -<scrollY>px).
      // Counteract it with an equal downward translate so the header stays at the
      // viewport top. Using transform — not position:fixed — keeps the header in
      // normal flow, so its space is preserved and page content below it doesn't
      // jump up behind it on non-home pages (where the header is in-flow sticky).
      const shift = -parseFloat(document.body.style.top || '0');
      wrapper.style.transform = `translateY(${Number.isFinite(shift) ? shift : 0}px)`;
    } else if (this.#scrollLocked) {
      wrapper.style.transform = '';
    }
    this.#scrollLocked = locked;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'scroll-up') {
      this.#observeStickyPosition(false);
      document.addEventListener('scroll', this.#handleWindowScroll);
    } else if (stickyMode === 'always') {
      // No IntersectionObserver here: the active state is computed from scroll
      // position (see #updateScrollState) so the header can pin at top:0.
      document.addEventListener('scroll', this.#handleWindowScroll);
      // Seed the initial state in case the page loads already scrolled.
      this.#updateScrollState();
    }

    // Hold the header at the viewport top while an overlay (cart / search)
    // scroll-locks the page by pinning <body> with position:fixed.
    if (stickyMode === 'always' || stickyMode === 'scroll-up') {
      this.#scrollLockObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
      this.#syncScrollLock();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#scrollLockObserver.disconnect();
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (entry.target !== header) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});